<template>
  <div>
    <v-skeleton-loader v-if="loading && !overview" type="card, card" />
    <template v-else-if="overview">
      <div v-if="isEstimated" class="mb-2">
        <v-chip size="x-small" color="secondary" prepend-icon="mdi-approximately-equal">
          {{ $t("ncrm.emailMarketing.estimatedFromEvents") }}
        </v-chip>
      </div>
      <NcrmHomeKpiCards :items="kpiItems" />

      <v-row dense class="mt-2">
        <v-col cols="12">
          <NcrmEmailMarketingTimelineChart :timeline="overview.timeline || []" :loading="loading" />
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="12" lg="6">
          <v-card class="pa-4 h-100" variant="flat">
            <div class="ncrm-em-dashboard__section-title">
              <v-icon size="15" class="mr-1" style="opacity: 0.62">mdi-filter-multiple-outline</v-icon>
              {{ $t("ncrm.emailMarketing.dashboard.funnelTitle") }}
            </div>
            <div class="ncrm-em-dashboard__funnel">
              <div v-for="step in funnelSteps" :key="step.key" class="ncrm-em-dashboard__funnel-row">
                <span class="ncrm-em-dashboard__funnel-label">{{ step.label }}</span>
                <span class="ncrm-em-dashboard__funnel-value">{{ formatNumber(step.value) }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6">
          <v-card class="pa-4 h-100" variant="flat">
            <div class="ncrm-em-dashboard__section-title">
              <v-icon size="15" class="mr-1" style="opacity: 0.62">mdi-trophy-outline</v-icon>
              {{ $t("ncrm.emailMarketing.dashboard.topCampaignsTitle") }}
            </div>
            <div v-if="topCampaigns.length" class="ncrm-em-dashboard__campaign-list">
              <div v-for="campaign in topCampaigns" :key="campaign.id" class="ncrm-em-dashboard__campaign-row">
                <span class="ncrm-em-dashboard__campaign-subject">{{ campaign.subject || "—" }}</span>
                <span class="ncrm-em-dashboard__campaign-meta">
                  {{ formatPercent(campaign.rates?.openRate) }} open ·
                  {{ formatPercent(campaign.rates?.clickRate) }} click ·
                  {{ campaign.leadsAdded }} {{ $t("ncrm.emailMarketing.dashboard.leadsShort") }}
                </span>
                <v-chip size="x-small" :color="scoreColor(campaign.score)">
                  {{ campaign.score ?? "—" }}
                </v-chip>
              </div>
            </div>
            <div v-else class="ncrm-em-dashboard__empty">
              <v-icon size="22" style="opacity: 0.35">mdi-inbox-outline</v-icon>
              <span>{{ $t("ncrm.emailMarketing.dashboard.noCampaigns") }}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="riskCampaigns.length" dense class="mt-2">
        <v-col cols="12">
          <v-card class="pa-4" variant="flat">
            <div class="ncrm-em-dashboard__section-title">
              <v-icon size="15" class="mr-1" color="error" style="opacity: 0.85">mdi-alert-outline</v-icon>
              {{ $t("ncrm.emailMarketing.dashboard.riskCampaignsTitle") }}
            </div>
            <div class="ncrm-em-dashboard__campaign-list">
              <div v-for="campaign in riskCampaigns" :key="campaign.id" class="ncrm-em-dashboard__campaign-row ncrm-em-dashboard__campaign-row--risk">
                <span class="ncrm-em-dashboard__campaign-subject">{{ campaign.subject || "—" }}</span>
                <span class="ncrm-em-dashboard__campaign-meta">
                  {{ formatPercent(campaign.rates?.bounceRate) }} bounce ·
                  {{ formatPercent(campaign.rates?.unsubRate) }} unsub
                </span>
                <v-chip size="x-small" :color="scoreColor(campaign.score)">
                  {{ campaign.score ?? "—" }}
                </v-chip>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
    <div v-else class="ncrm-em-dashboard__empty pa-8">
      <v-icon size="22" style="opacity: 0.35">mdi-email-off-outline</v-icon>
      <span>{{ $t("ncrm.emailMarketing.dashboard.empty") }}</span>
    </div>
  </div>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmEmailMarketingTimelineChart from "./NcrmEmailMarketingTimelineChart.vue";
import { formatPercent, resolveScoreColor } from "@/Utile/ncrmEmailMarketing.mjs";
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmEmailMarketingDashboardTab",
  components: { NcrmHomeKpiCards, NcrmEmailMarketingTimelineChart },
  props: {
    overview: { type: Object, default: null },
    campaigns: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  computed: {
    isEstimated() {
      return this.overview?.totals?.metricsSource === "events";
    },
    kpiItems() {
      const totals = this.overview?.totals || {};
      const rates = totals.rates || {};
      const events = this.overview?.events || {};
      return [
        {
          key: "delivered",
          label: this.$t("ncrm.emailMarketing.kpis.delivered"),
          value: totals.delivered,
          icon: "mdi-email-check-outline",
          color: "primary",
        },
        {
          key: "openRate",
          label: this.$t("ncrm.emailMarketing.kpis.openRate"),
          displayValue: formatPercent(rates.openRate),
          icon: "mdi-email-open-outline",
          color: "info",
        },
        {
          key: "clickRate",
          label: this.$t("ncrm.emailMarketing.kpis.clickRate"),
          displayValue: formatPercent(rates.clickRate),
          icon: "mdi-cursor-default-click-outline",
          color: "success",
        },
        {
          key: "bounceRate",
          label: this.$t("ncrm.emailMarketing.kpis.bounceRate"),
          displayValue: formatPercent(rates.bounceRate),
          icon: "mdi-email-remove-outline",
          color: "warning",
        },
        {
          key: "uniqueOpens",
          label: this.$t("ncrm.emailMarketing.kpis.uniqueOpens"),
          value: events.uniqueOpens,
          icon: "mdi-account-eye-outline",
          color: "info",
        },
        {
          key: "uniqueClicks",
          label: this.$t("ncrm.emailMarketing.kpis.uniqueClicks"),
          value: events.uniqueClicks,
          icon: "mdi-account-check-outline",
          color: "success",
        },
      ];
    },
    funnelSteps() {
      const funnel = this.overview?.funnel || {};
      return [
        { key: "engagedContacts", label: this.$t("ncrm.emailMarketing.funnel.engagedContacts"), value: funnel.engagedContacts },
        { key: "engagedRows", label: this.$t("ncrm.emailMarketing.funnel.engagedRows"), value: funnel.engagedRows },
        { key: "leadsAdded", label: this.$t("ncrm.emailMarketing.funnel.leadsAdded"), value: funnel.leadsAdded },
        { key: "dealsCount", label: this.$t("ncrm.emailMarketing.funnel.deals"), value: funnel.dealsCount },
        { key: "contractsCount", label: this.$t("ncrm.emailMarketing.funnel.contracts"), value: funnel.contractsCount },
      ];
    },
    topCampaigns() {
      return [...this.campaigns]
        .filter((campaign) => campaign.score !== null && campaign.score !== undefined)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
    },
    riskCampaigns() {
      return this.campaigns.filter((campaign) => campaign.atRisk).slice(0, 5);
    },
  },
  methods: {
    formatPercent,
    scoreColor: resolveScoreColor,
    formatNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? formatDisplayNumber(parsed) : "—";
    },
  },
};
</script>

<style scoped>
.ncrm-em-dashboard__section-title {
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-secondary-button-text, rgb(var(--v-theme-secondary)));
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
  padding-bottom: 6px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.ncrm-em-dashboard__funnel-row,
.ncrm-em-dashboard__campaign-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}

.ncrm-em-dashboard__funnel-row:last-child,
.ncrm-em-dashboard__campaign-row:last-child {
  border-bottom: none;
}

.ncrm-em-dashboard__funnel-label {
  flex: 1 1 auto;
  font-size: 0.88rem;
  opacity: 0.68;
}

.ncrm-em-dashboard__funnel-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--ncrm-color-text-strong);
}

.ncrm-em-dashboard__campaign-subject {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--ncrm-color-text-strong);
}

.ncrm-em-dashboard__campaign-meta {
  font-size: 0.8rem;
  opacity: 0.6;
  white-space: nowrap;
}

.ncrm-em-dashboard__campaign-row--risk .ncrm-em-dashboard__campaign-subject {
  color: rgb(var(--v-theme-error));
}

.ncrm-em-dashboard__empty {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  opacity: 0.5;
  padding: 24px 0;
}
</style>
